import type { QueryKey } from '@tanstack/react-query';
import type { PostgrestError, SupabaseClient } from '@supabase/supabase-js';
import { getSupabaseClient } from './supabase';

type QueryOrder = { column: string; ascending?: boolean; nullsFirst?: boolean };

export type SearchItemsParams = {
  supabase?: SupabaseClient;
  term: string;
  select?: string;
  limit?: number;
  itemTypeId?: string | null;
  searchColumns?: string[];
};

export type ActiveInventorySku = {
  item_id: string;
  item_code: string;
  name_internal: string;
  brand?: string | null;
  manufacturer_name?: string | null;
  model_name?: string | null;
  model_number?: string | null;
  purchase_cost_last?: number | null;
  purchase_cost_avg?: number | null;
  replacement_cost?: number | null;
  is_kit?: boolean | null;
};

export type SearchProductsParams = {
  supabase?: SupabaseClient;
  term: string;
  select?: string;
  limit?: number;
};

export type FetchItemsListParams = {
  supabase?: SupabaseClient;
  select: string;
  view?: 'all' | 'active' | 'archived';
  order?: QueryOrder;
  range?: { from: number; to: number };
  restrictType?: { field: string; value: string } | null;
};

export type CountItemsParams = {
  supabase?: SupabaseClient;
  filters?: Array<{ column: string; op: 'eq' | 'is' | 'not'; value: string | null | boolean }>;
};

export type CountProductsParams = {
  supabase?: SupabaseClient;
  status?: string | null;
  visibleToClient?: boolean | null;
  categoryId?: string | null;
};

export async function fetchCategoryChargeDefaults(
  options?: { supabase?: SupabaseClient },
): Promise<{ data: Record<string, number | null>; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase.from('categories').select('category_id,default_charge_days');
  if (error) return { data: {}, error };
  const map: Record<string, number | null> = {};
  for (const row of (data ?? []) as Array<{ category_id: string; default_charge_days: number | null }>) {
    if (!row || !row.category_id) continue;
    map[String(row.category_id)] =
      row.default_charge_days !== null && row.default_charge_days !== undefined
        ? Number(row.default_charge_days)
        : null;
  }
  return { data: map, error: null };
}

export type FetchCategoriesParams = {
  supabase?: SupabaseClient;
  select?: string;
};

export type FetchItemsPageParams = {
  supabase?: SupabaseClient;
  select: string;
  view?: 'all' | 'active' | 'archived';
  order?: QueryOrder;
  range?: { from: number; to: number };
  filters?: Array<{ column: string; op: 'eq' | 'ilike' | 'is' | 'gt' | 'lt' | 'gte' | 'lte' | 'in'; value: any }>;
  searchTerm?: string | null;
  count?: 'exact' | 'planned' | 'estimated' | null;
};

export type FetchAllItemsPagesParams = Omit<FetchItemsPageParams, 'range' | 'count'> & {
  pageSize?: number;
  maxRows?: number;
};

export type FetchProductsPageParams = {
  supabase?: SupabaseClient;
  select: string;
  order?: QueryOrder;
  range?: { from: number; to: number };
  filters?: Array<{ column: string; op: 'eq' | 'ilike' | 'is' | 'gt' | 'lt' | 'gte' | 'lte' | 'in'; value: any }>;
};

export type FetchItemTypesParams = {
  supabase?: SupabaseClient;
  select?: string;
};

export type FetchCategoriesPageParams = {
  supabase?: SupabaseClient;
  select?: string;
  order?: QueryOrder;
  range?: { from: number; to: number };
  search?: string | null;
};

export type CountCategoriesParams = {
  supabase?: SupabaseClient;
};

export async function searchItems(params: SearchItemsParams): Promise<{ data: any[]; error: PostgrestError | null }> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { term, select, limit = 10, itemTypeId, searchColumns } = params;
  const query = supabase
    .from('items')
    .select(select ?? 'item_id,item_code,name_internal', { count: 'exact' })
    .limit(limit);

  const trimmed = term.trim();
  if (trimmed) {
    const fields = searchColumns && searchColumns.length ? searchColumns : ['item_code', 'name_internal'];
    const pattern = `%${trimmed}%`;
    query.or(fields.map(field => `${field}.ilike.${pattern}`).join(','));
  }

  if (itemTypeId) {
    query.eq('item_type_id', itemTypeId);
  }

  const { data, error } = await query;
  return { data: Array.isArray(data) ? data : [], error };
}

/**
 * Lightweight company-wide SKU lookup for inline editors.
 *
 * Archived items are deliberately excluded, while active items with no current
 * stock remain searchable because a planning scratch pad may be documenting a
 * need precisely because inventory is unavailable.
 */
export async function searchActiveInventorySkus(params: {
  supabase?: SupabaseClient;
  term: string;
  limit?: number;
}): Promise<{ data: ActiveInventorySku[]; error: PostgrestError | null }> {
  const supabase = params.supabase ?? getSupabaseClient();
  const limit = Math.max(1, Math.min(25, Math.trunc(Number(params.limit) || 12)));
  const term = String(params.term || '')
    .replace(/[%(),]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 80);
  if (!term) return { data: [], error: null };

  const pattern = `%${term.replace(/\s+/g, '%')}%`;
  const { data, error } = await supabase
    .from('items')
    .select(
      'item_id,item_code,name_internal,brand,manufacturer_name,model_name,model_number,purchase_cost_last,purchase_cost_avg,replacement_cost,is_kit',
    )
    .is('deleted_at', null)
    .or(`item_code.ilike.${pattern},name_internal.ilike.${pattern}`)
    .limit(limit);

  if (error) return { data: [], error };
  const normalizedTerm = term.toLocaleLowerCase();
  const rows = (Array.isArray(data) ? data : [])
    .filter((row) => row?.item_id && row?.item_code)
    .map((row) => ({
      ...row,
      item_id: String(row.item_id),
      item_code: String(row.item_code),
      name_internal: String(row.name_internal || ''),
    })) as ActiveInventorySku[];

  const rank = (row: ActiveInventorySku): number => {
    const code = row.item_code.toLocaleLowerCase();
    const name = row.name_internal.toLocaleLowerCase();
    if (code === normalizedTerm) return 0;
    if (code.startsWith(normalizedTerm)) return 1;
    if (code.includes(normalizedTerm)) return 2;
    if (name.startsWith(normalizedTerm)) return 3;
    return 4;
  };

  rows.sort((left, right) => {
    const byRank = rank(left) - rank(right);
    if (byRank !== 0) return byRank;
    return left.item_code.localeCompare(right.item_code, undefined, { numeric: true, sensitivity: 'base' });
  });
  return { data: rows, error: null };
}

export async function searchProducts(params: SearchProductsParams): Promise<{ data: any[]; error: PostgrestError | null }> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { term, select, limit = 10 } = params;
  const query = supabase
    .from('products')
    .select(select ?? 'product_id,product_code,name_public')
    .limit(limit);

  const trimmed = term.trim();
  if (trimmed) {
    query.ilike('product_code', `${trimmed}%`);
  }

  const { data, error } = await query;
  return { data: Array.isArray(data) ? data : [], error };
}

export async function fetchItemIdByCode(params: {
  supabase?: SupabaseClient;
  itemCode: string;
  itemTypeId?: string | null;
}): Promise<{ itemId: string | null; error: PostgrestError | null }> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { itemCode, itemTypeId } = params;
  const trimmed = itemCode.trim();
  if (!trimmed) return { itemId: null, error: null };
  let query = supabase.from('items').select('item_id').eq('item_code', trimmed);
  if (itemTypeId) {
    query = query.eq('item_type_id', itemTypeId);
  }
  const { data, error } = await query.maybeSingle();
  if (error) return { itemId: null, error };
  const itemId = (data as any)?.item_id ? String((data as any).item_id) : null;
  return { itemId, error: null };
}

export async function fetchItemByCode(params: {
  supabase?: SupabaseClient;
  itemCode: string;
  select?: string;
  itemTypeId?: string | null;
}): Promise<{ data: any | null; error: PostgrestError | null }> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { itemCode, select, itemTypeId } = params;
  const trimmed = itemCode.trim();
  if (!trimmed) return { data: null, error: null };
  let query = supabase.from('items').select(select ?? 'item_id,item_code,name_internal').eq('item_code', trimmed);
  if (itemTypeId) {
    query = query.eq('item_type_id', itemTypeId);
  }
  const { data, error } = await query.maybeSingle();
  return { data: data ?? null, error: error ?? null };
}

export async function fetchItemsByIds(params: {
  supabase?: SupabaseClient;
  itemIds: string[];
  select?: string;
}): Promise<{ data: any[]; error: PostgrestError | null }> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { itemIds, select } = params;
  if (!itemIds.length) return { data: [], error: null };
  const { data, error } = await supabase
    .from('items')
    .select(select ?? 'item_id,item_code,name_internal,category_id')
    .in('item_id', itemIds);
  return { data: Array.isArray(data) ? data : [], error: error ?? null };
}

export async function fetchItemsList(params: FetchItemsListParams): Promise<{
  data: any[];
  count: number | null;
  error: PostgrestError | null;
}> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { select, view = 'all', order, range, restrictType } = params;
  let query = supabase.from('items').select(select, { count: 'exact' });
  if (view === 'active') query = query.is('deleted_at', null);
  if (view === 'archived') query = query.not('deleted_at', 'is', null);
  if (restrictType?.field && restrictType.value) {
    query = query.eq(restrictType.field, restrictType.value);
  }
  if (order) {
    query = query.order(order.column, { ascending: order.ascending ?? true, nullsFirst: order.nullsFirst ?? false });
  }
  if (range) {
    query = query.range(range.from, range.to);
  }
  const { data, error, count } = await query;
  return { data: Array.isArray(data) ? data : [], count: count ?? null, error: error ?? null };
}

export async function fetchItemsPage(params: FetchItemsPageParams): Promise<{
  data: any[];
  count: number | null;
  error: PostgrestError | null;
}> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { select, view = 'all', order, range, filters, searchTerm, count: countMode = 'exact' } = params;
  let query = countMode === null
    ? supabase.from('items').select(select)
    : supabase.from('items').select(select, { count: countMode });
  if (view === 'active') query = query.is('deleted_at', null);
  if (view === 'archived') query = query.not('deleted_at', 'is', null);
  const trimmedSearch = searchTerm?.trim();
  if (trimmedSearch) {
    // PostgREST accepts * as wildcard alias for % to avoid URL-encoding issues
    const pattern = `*${trimmedSearch}*`;
    query = query.or(`item_code.ilike.${pattern},name_internal.ilike.${pattern}`);
  }
  for (const filter of filters ?? []) {
    if (filter.op === 'eq') query = query.eq(filter.column, filter.value);
    if (filter.op === 'ilike') query = query.ilike(filter.column, filter.value);
    if (filter.op === 'is') query = query.is(filter.column, filter.value);
    if (filter.op === 'gt') query = query.gt(filter.column, filter.value);
    if (filter.op === 'lt') query = query.lt(filter.column, filter.value);
    if (filter.op === 'gte') query = query.gte(filter.column, filter.value);
    if (filter.op === 'lte') query = query.lte(filter.column, filter.value);
    if (filter.op === 'in') query = query.in(filter.column, filter.value);
  }
  if (order) {
    query = query.order(order.column, { ascending: order.ascending ?? true, nullsFirst: order.nullsFirst ?? false });
  }
  if (range) {
    query = query.range(range.from, range.to);
  }
  const { data, error, count } = await query;
  return { data: Array.isArray(data) ? data : [], count: count ?? null, error: error ?? null };
}

export async function fetchAllItemsPages(params: FetchAllItemsPagesParams): Promise<{
  data: any[];
  count: number | null;
  error: PostgrestError | null;
}> {
  const pageSize = Math.max(1, Math.min(params.pageSize ?? 1000, 5000));
  const allRows: any[] = [];
  let totalCount: number | null = null;

  while (true) {
    if (params.maxRows != null && allRows.length >= params.maxRows) {
      return { data: allRows.slice(0, params.maxRows), count: totalCount, error: null };
    }

    const from = allRows.length;
    const to = from + pageSize - 1;
    const { data, count, error } = await fetchItemsPage({
      ...params,
      range: { from, to },
      count: totalCount === null ? 'exact' : null,
    });

    if (error) return { data: allRows, count: totalCount, error };
    const rows = Array.isArray(data) ? data : [];
    if (totalCount === null) totalCount = count ?? null;
    if (rows.length === 0) return { data: allRows, count: totalCount, error: null };

    allRows.push(...rows);

    if (rows.length < pageSize) return { data: allRows, count: totalCount, error: null };
    if (totalCount !== null && allRows.length >= totalCount) return { data: allRows, count: totalCount, error: null };
  }
}

export async function countItems(params: CountItemsParams): Promise<{ count: number | null; error: PostgrestError | null }> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { filters } = params;
  let query = supabase.from('items').select('item_id', { count: 'exact', head: true });
  for (const filter of filters ?? []) {
    if (filter.op === 'eq') query = query.eq(filter.column, filter.value as any);
    if (filter.op === 'is') query = query.is(filter.column, filter.value as any);
    if (filter.op === 'not') query = query.not(filter.column, 'is', filter.value as any);
  }
  const { error, count } = await query;
  return { count: count ?? null, error: error ?? null };
}

export async function updateItemDeletion(params: {
  supabase?: SupabaseClient;
  itemId: string;
  deletedAt: string | null;
}): Promise<PostgrestError | null> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { itemId, deletedAt } = params;
  const { error } = await supabase.from('items').update({ deleted_at: deletedAt }).eq('item_id', itemId);
  return error ?? null;
}

export async function reclassifyItaKitItemsAsKits(params?: {
  supabase?: SupabaseClient;
}): Promise<{ updated: number; error: PostgrestError | null }> {
  const supabase = params?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase
    .from('items')
    .update({ is_kit: true })
    .ilike('item_code', 'ITA-KIT%')
    .or('is_kit.is.null,is_kit.eq.false')
    .select('item_id');

  return { updated: Array.isArray(data) ? data.length : 0, error: error ?? null };
}

export async function upsertItem(params: {
  supabase?: SupabaseClient;
  payload: Record<string, any> | Record<string, any>[];
  onConflict?: string;
}): Promise<PostgrestError | null> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { payload, onConflict } = params;
  const { error } = await supabase.from('items').upsert(payload, onConflict ? { onConflict } : undefined);
  return error ?? null;
}

export async function updateItem(params: {
  supabase?: SupabaseClient;
  itemId: string;
  payload: Record<string, any>;
}): Promise<PostgrestError | null> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { itemId, payload } = params;
  const { error } = await supabase.from('items').update(payload).eq('item_id', itemId);
  return error ?? null;
}

export async function fetchItemBasics(params: {
  supabase?: SupabaseClient;
  itemId: string;
  select?: string;
}): Promise<{ data: any | null; error: PostgrestError | null }> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { itemId, select } = params;
  const { data, error } = await supabase
    .from('items')
    .select(select ?? 'item_id,item_code,name_internal')
    .eq('item_id', itemId)
    .maybeSingle();
  return { data: data ?? null, error: error ?? null };
}

export async function clearItemsCategory(params: {
  supabase?: SupabaseClient;
  categoryId: string;
}): Promise<PostgrestError | null> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { categoryId } = params;
  const { error } = await supabase.from('items').update({ category_id: null }).eq('category_id', categoryId);
  return error ?? null;
}

export async function clearProductsCategory(params: {
  supabase?: SupabaseClient;
  categoryId: string;
}): Promise<PostgrestError | null> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { categoryId } = params;
  const { error } = await supabase.from('products').update({ category_id: null }).eq('category_id', categoryId);
  return error ?? null;
}

export async function countProducts(params: CountProductsParams): Promise<{ count: number | null; error: PostgrestError | null }> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { status, visibleToClient, categoryId } = params;
  let query = supabase.from('products').select('product_id', { count: 'exact', head: true });
  if (status) query = query.eq('status', status);
  if (visibleToClient != null) query = query.eq('visible_to_client', visibleToClient);
  if (categoryId) query = query.eq('category_id', categoryId);
  const { count, error } = await query;
  return { count: count ?? null, error: error ?? null };
}

export async function fetchProductsPage(params: FetchProductsPageParams): Promise<{ data: any[]; count: number | null; error: PostgrestError | null }> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { select, order, range, filters } = params;
  let query = supabase.from('products').select(select, { count: 'exact' });
  for (const filter of filters ?? []) {
    if (filter.op === 'eq') query = query.eq(filter.column, filter.value);
    if (filter.op === 'ilike') query = query.ilike(filter.column, filter.value);
    if (filter.op === 'is') query = query.is(filter.column, filter.value);
    if (filter.op === 'gt') query = query.gt(filter.column, filter.value);
    if (filter.op === 'lt') query = query.lt(filter.column, filter.value);
    if (filter.op === 'gte') query = query.gte(filter.column, filter.value);
    if (filter.op === 'lte') query = query.lte(filter.column, filter.value);
    if (filter.op === 'in') query = query.in(filter.column, filter.value);
  }
  if (order) {
    query = query.order(order.column, { ascending: order.ascending ?? true, nullsFirst: order.nullsFirst ?? false });
  }
  if (range) {
    query = query.range(range.from, range.to);
  }
  const { data, error, count } = await query;
  return { data: Array.isArray(data) ? data : [], count: count ?? null, error: error ?? null };
}

export async function fetchProducts(params: {
  supabase?: SupabaseClient;
  select: string;
  order?: QueryOrder;
  range?: { from: number; to: number };
  filters?: Array<{ column: string; op: 'eq' | 'ilike'; value: any }>;
}): Promise<{ data: any[]; error: PostgrestError | null }> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { select, order, range, filters } = params;
  let query = supabase.from('products').select(select);
  for (const filter of filters ?? []) {
    if (filter.op === 'eq') query = query.eq(filter.column, filter.value);
    if (filter.op === 'ilike') query = query.ilike(filter.column, filter.value);
  }
  if (order) {
    query = query.order(order.column, { ascending: order.ascending ?? true, nullsFirst: order.nullsFirst ?? false });
  }
  if (range) {
    query = query.range(range.from, range.to);
  }
  const { data, error } = await query;
  return { data: Array.isArray(data) ? data : [], error: error ?? null };
}

export async function fetchProductById(params: {
  supabase?: SupabaseClient;
  productId: string;
  select?: string;
}): Promise<{ data: any | null; error: PostgrestError | null }> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { productId, select } = params;
  const { data, error } = await supabase
    .from('products')
    .select(select ?? 'product_id,product_code,name_public,short_description,default_charge_days,facets')
    .eq('product_id', productId)
    .maybeSingle();
  return { data: data ?? null, error: error ?? null };
}

export async function fetchProductByCode(params: {
  supabase?: SupabaseClient;
  productCode: string;
  select?: string;
}): Promise<{ data: any | null; error: PostgrestError | null }> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { productCode, select } = params;
  const trimmed = productCode.trim();
  if (!trimmed) return { data: null, error: null };
  const { data, error } = await supabase
    .from('products')
    .select(select ?? 'product_id,name_public,product_code')
    .eq('product_code', trimmed)
    .maybeSingle();
  return { data: data ?? null, error: error ?? null };
}

export async function fetchItemTypes(params: FetchItemTypesParams): Promise<{ data: any[]; error: PostgrestError | null }> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { select } = params;
  const { data, error } = await supabase.from('item_types').select(select ?? '*');
  return { data: Array.isArray(data) ? data : [], error: error ?? null };
}

export async function fetchCategories(params: FetchCategoriesParams): Promise<{ data: any[]; error: PostgrestError | null }> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { select } = params;
  const { data, error } = await supabase.from('categories').select(select ?? 'category_id,name').order('name', { ascending: true });
  return { data: Array.isArray(data) ? data : [], error: error ?? null };
}

export async function fetchActiveProductsForSearch(options?: { supabase?: SupabaseClient }): Promise<{
  data: any[];
  error: PostgrestError | null;
}> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase
    .from('products')
    .select('product_id,name_public,category_id,status,default_charge_days')
    .eq('status', 'active')
    .order('name_public', { ascending: true });
  return { data: Array.isArray(data) ? data : [], error: error ?? null };
}

export type ItemSearchFiltersResult = {
  categories: Array<{ id: string; name: string }>;
  products: Array<{ id: string; name: string; categoryId: string | null; defaultChargeDays: number | null }>;
};

export const itemSearchFiltersQueryKey: QueryKey = ['item-search-filters'];

export const getItemSearchFiltersQueryOptions = ({
  supabase = getSupabaseClient(),
  enabled = true,
}: { supabase?: SupabaseClient; enabled?: boolean } = {}) => ({
  queryKey: itemSearchFiltersQueryKey,
  enabled,
  queryFn: async (): Promise<ItemSearchFiltersResult> => {
    const [categoriesRes, productsRes] = await Promise.all([
      fetchCategories({ supabase, select: 'category_id,name' }),
      fetchActiveProductsForSearch({ supabase }),
    ]);
    if (categoriesRes.error) throw categoriesRes.error;
    if (productsRes.error) throw productsRes.error;
    const categories = (Array.isArray(categoriesRes.data) ? categoriesRes.data : [])
      .map(row => {
        const id = row?.category_id != null ? String(row.category_id) : '';
        if (!id) return null;
        const rawName = typeof row?.name === 'string' ? row.name.trim() : '';
        return { id, name: rawName || 'Uncategorized' };
      })
      .filter((row): row is { id: string; name: string } => !!row);
    const products = (Array.isArray(productsRes.data) ? productsRes.data : [])
      .map(row => {
        const id = row?.product_id != null ? String(row.product_id) : '';
        if (!id) return null;
        const name = typeof row?.name_public === 'string' ? row.name_public.trim() : '';
        const categoryId = row?.category_id != null ? String(row.category_id) : null;
        const chargeDaysRaw = row?.default_charge_days;
        const chargeDays = typeof chargeDaysRaw === 'number'
          ? chargeDaysRaw
          : typeof chargeDaysRaw === 'string' && chargeDaysRaw.trim()
            ? Number(chargeDaysRaw)
            : null;
        return {
          id,
          name: name || 'Untitled product',
          categoryId,
          defaultChargeDays: Number.isFinite(chargeDays) ? Number(chargeDays) : null,
        };
      })
      .filter((row): row is { id: string; name: string; categoryId: string | null; defaultChargeDays: number | null } => !!row);
    return { categories, products };
  },
});

export async function fetchCategoriesPage(params: FetchCategoriesPageParams): Promise<{ data: any[]; count: number | null; error: PostgrestError | null }> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { select, order, range, search } = params;
  let query = supabase.from('categories').select(select ?? 'category_id,name,default_charge_days,created_at,updated_at', { count: 'exact' });
  if (search && search.trim()) {
    const pattern = `%${search.trim()}%`;
    query = query.ilike('name', pattern);
  }
  if (order) {
    query = query.order(order.column, { ascending: order.ascending ?? true, nullsFirst: order.nullsFirst ?? false });
  }
  if (range) {
    query = query.range(range.from, range.to);
  }
  const { data, error, count } = await query;
  return { data: Array.isArray(data) ? data : [], count: count ?? null, error: error ?? null };
}

export async function countCategories(params: CountCategoriesParams): Promise<{ count: number | null; error: PostgrestError | null }> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { count, error } = await supabase.from('categories').select('category_id', { count: 'exact', head: true });
  return { count: count ?? null, error: error ?? null };
}

export async function fetchCategoryByName(params: { supabase?: SupabaseClient; name: string; select?: string }): Promise<{ data: any | null; error: PostgrestError | null }> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { name, select } = params;
  const trimmed = name.trim();
  if (!trimmed) return { data: null, error: null };
  const { data, error } = await supabase.from('categories').select(select ?? 'category_id,name').eq('name', trimmed).maybeSingle();
  return { data: data ?? null, error: error ?? null };
}

export async function fetchCategoryById(params: {
  supabase?: SupabaseClient;
  categoryId: string;
  select?: string;
}): Promise<{ data: any | null; error: PostgrestError | null }> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { categoryId, select } = params;
  const { data, error } = await supabase
    .from('categories')
    .select(select ?? 'category_id,name,default_charge_days,created_at,updated_at')
    .eq('category_id', categoryId)
    .maybeSingle();
  return { data: data ?? null, error: error ?? null };
}

export async function upsertCategory(params: {
  supabase?: SupabaseClient;
  payload: Record<string, any> | Record<string, any>[];
  onConflict?: string;
}): Promise<PostgrestError | null> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { payload, onConflict } = params;
  const { error } = await supabase.from('categories').upsert(payload, onConflict ? { onConflict } : undefined);
  return error ?? null;
}

export async function deleteCategory(params: { supabase?: SupabaseClient; categoryId: string }): Promise<PostgrestError | null> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { categoryId } = params;
  const { error } = await supabase.from('categories').delete().eq('category_id', categoryId);
  return error ?? null;
}

export async function insertProduct(params: {
  supabase?: SupabaseClient;
  payload: Record<string, any>;
}): Promise<PostgrestError | null> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { payload } = params;
  const { error } = await supabase.from('products').insert(payload);
  return error ?? null;
}

export async function updateProduct(params: {
  supabase?: SupabaseClient;
  productId: string;
  payload: Record<string, any>;
}): Promise<PostgrestError | null> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { productId, payload } = params;
  const { error } = await supabase.from('products').update(payload).eq('product_id', productId);
  return error ?? null;
}

export async function deleteProduct(params: {
  supabase?: SupabaseClient;
  productId: string;
}): Promise<PostgrestError | null> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { productId } = params;
  const { error } = await supabase.from('products').delete().eq('product_id', productId);
  return error ?? null;
}

// Lightweight query builders for legacy callers migrating off direct Supabase usage.
export function itemsQuery(supabase: SupabaseClient = getSupabaseClient()) {
  return supabase.from('items');
}

export function productsQuery(supabase: SupabaseClient = getSupabaseClient()) {
  return supabase.from('products');
}

export function categoriesQuery(supabase: SupabaseClient = getSupabaseClient()) {
  return supabase.from('categories');
}

export function productItemLinkQuery(supabase: SupabaseClient = getSupabaseClient()) {
  return supabase.from('product_item_link');
}

export async function fetchProductPickerResults(params: {
  supabase?: SupabaseClient;
  search?: string;
  limit?: number;
  select?: string;
}): Promise<{ data: any[]; error: PostgrestError | null }> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { search, limit = 50, select } = params;
  let query = productsQuery(supabase)
    .select(
      select
        ?? `
        product_id,
        name_public,
        short_description,
        same_item_required,
        facets,
        category_id,
        default_charge_days,
        default_item_id,
        default_item:items!products_default_item_id_fkey(item_id,item_code,name_internal,is_kit,kit_pricing_mode)
      `,
    )
    .eq('status', 'active')
    .order('name_public', { ascending: true })
    .limit(limit);
  const term = (search ?? '').trim();
  if (term) {
    const sanitized = term.replace(/[%]/g, '').replace(/,/g, ' ').trim();
    if (sanitized) {
      const pattern = `%${sanitized.replace(/\s+/g, '%')}%`;
      query = query.or(`name_public.ilike.${pattern},short_description.ilike.${pattern}`);
    }
  }
  const { data, error } = await query;
  return { data: Array.isArray(data) ? data : [], error: error ?? null };
}
export async function fetchProductsDefaultItems(params: {
  supabase?: SupabaseClient;
  productIds: string[];
}): Promise<{ data: any[]; error: PostgrestError | null }> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { productIds } = params;
  if (!productIds.length) return { data: [], error: null };
  const { data, error } = await supabase.from('products').select('product_id,default_item_id').in('product_id', productIds);
  return { data: Array.isArray(data) ? data : [], error: error ?? null };
}

export async function fetchProductSiteOverrides(params: {
  supabase?: SupabaseClient;
  productIds: string[];
  locationId: string;
}): Promise<{ data: any[]; error: PostgrestError | null }> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { productIds, locationId } = params;
  if (!productIds.length) return { data: [], error: null };
  const { data, error } = await supabase
    .from('product_site_overrides')
    .select('product_id,default_item_id')
    .eq('location_id', locationId)
    .in('product_id', productIds);
  return { data: Array.isArray(data) ? data : [], error: error ?? null };
}

export async function fetchProductItemLinks(params: {
  supabase?: SupabaseClient;
  productIds: string[];
  select?: string;
  orderByRank?: boolean;
}): Promise<{ data: any[]; error: PostgrestError | null }> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { productIds, select, orderByRank = true } = params;
  if (!productIds.length) return { data: [], error: null };
  let query = supabase.from('product_item_link').select(select ?? 'product_id,item_id,rank').in('product_id', productIds);
  if (orderByRank) query = query.order('rank', { ascending: true, nullsFirst: false });
  const { data, error } = await query;
  return { data: Array.isArray(data) ? data : [], error: error ?? null };
}

export async function searchProductItemLinks(params: {
  supabase?: SupabaseClient;
  select: string;
  limit?: number;
  productId?: string | null;
  categoryId?: string | null;
  inventoryLocationId?: string | null;
  productStatus?: string | null;
  itemTypeId?: string | number | null;
  excludeItemTypeId?: number;
  searchTerm?: string | null;
  itemSearchColumns?: string[];
  productSearchColumns?: string[];
  orderBy?: { column: string; ascending?: boolean; referencedTable?: string };
  /** When true, exclude items where items.is_kit is true (kit items are not searchable in the order Items tab). */
  excludeKitItems?: boolean;
}): Promise<{ data: any[]; error: PostgrestError | null }> {
  const {
    supabase: supabaseOption,
    select,
    limit = 100,
    productId,
    categoryId,
    inventoryLocationId,
    productStatus,
    itemTypeId,
    excludeItemTypeId,
    searchTerm,
    itemSearchColumns,
    productSearchColumns,
    orderBy,
    excludeKitItems,
  } = params;
  const supabase = supabaseOption ?? getSupabaseClient();

  let query = supabase.from('product_item_link').select(select).limit(limit);

  if (productStatus) query = query.eq('products.status', productStatus);
  if (inventoryLocationId) query = query.eq('items.item_location_stock.location_id', inventoryLocationId);
  if (productId) query = query.eq('product_id', productId);
  if (categoryId) query = query.eq('products.category_id', categoryId);
  if (itemTypeId != null) query = query.eq('items.item_type_id', itemTypeId);
  const excludedItemTypeClause = excludeItemTypeId != null
    ? `or(item_type_id.is.null,item_type_id.neq.${excludeItemTypeId})`
    : null;
  if (excludedItemTypeClause) query = query.or(excludedItemTypeClause, { referencedTable: 'items' });
  if (excludeKitItems) query = query.eq('items.is_kit', false);

  // Strip % and replace comma/ampersand with space so URL and ilike behave: & would break query params.
  const sanitizedTerm = (searchTerm ?? '')
    .replace(/[%]/g, '')
    .replace(/,/g, ' ')
    .replace(/&/g, ' ')
    .trim()
    .replace(/\s+/g, ' ')
    .trim();
  const itemColumns = itemSearchColumns && itemSearchColumns.length ? itemSearchColumns : ['name_internal', 'item_code'];
  const productColumns = productSearchColumns && productSearchColumns.length ? productSearchColumns : [];

  if (sanitizedTerm && itemColumns.length && productColumns.length) {
    const pattern = `%${sanitizedTerm.replace(/\s+/g, '%')}%`;
    const itemSearchClause = itemColumns.map(col => `${col}.ilike.${pattern}`).join(',');
    const itemClause = excludedItemTypeClause
      ? `and(${excludedItemTypeClause},or(${itemSearchClause}))`
      : itemSearchClause;
    const productClause = productColumns.map(col => `${col}.ilike.${pattern}`).join(',');
    const buildBase = () => {
      let q = supabase.from('product_item_link').select(select).limit(limit);
      if (productStatus) q = q.eq('products.status', productStatus);
      if (inventoryLocationId) q = q.eq('items.item_location_stock.location_id', inventoryLocationId);
      if (productId) q = q.eq('product_id', productId);
      if (categoryId) q = q.eq('products.category_id', categoryId);
      if (itemTypeId != null) q = q.eq('items.item_type_id', itemTypeId);
      if (excludedItemTypeClause) q = q.or(excludedItemTypeClause, { referencedTable: 'items' });
      if (excludeKitItems) q = q.eq('items.is_kit', false);
      return q;
    };
    const [resItem, resProduct] = await Promise.all([
      buildBase().or(itemClause, { referencedTable: 'items' }),
      buildBase().or(productClause, { referencedTable: 'products' }),
    ]);
    const byKey = new Map<string, any>();
    const add = (res: { data?: any[] }) => {
      const rows = Array.isArray(res?.data) ? res.data : [];
      for (const row of rows) {
        const pid = row?.product_id;
        const item = row?.items;
        const iid = item?.item_id ?? (Array.isArray(item) ? item[0]?.item_id : null);
        if (pid != null && iid != null) byKey.set(`${pid}:${iid}`, row);
      }
    };
    add(resItem as { data?: any[] });
    add(resProduct as { data?: any[] });
    let merged = Array.from(byKey.values()).slice(0, limit);
    const errItem = (resItem as { error?: PostgrestError | null }).error;
    const errProduct = (resProduct as { error?: PostgrestError | null }).error;
    const err = errItem ?? errProduct ?? null;
    if (merged.length === 0 && !err) {
      let fallbackQuery = supabase.from('product_item_link').select(select).limit(limit);
      if (productStatus) fallbackQuery = fallbackQuery.eq('products.status', productStatus);
      if (inventoryLocationId) fallbackQuery = fallbackQuery.eq('items.item_location_stock.location_id', inventoryLocationId);
      if (productId) fallbackQuery = fallbackQuery.eq('product_id', productId);
      if (categoryId) fallbackQuery = fallbackQuery.eq('products.category_id', categoryId);
      if (itemTypeId != null) fallbackQuery = fallbackQuery.eq('items.item_type_id', itemTypeId);
      if (excludeKitItems) fallbackQuery = fallbackQuery.eq('items.is_kit', false);
      const fallback = await fallbackQuery.or(itemClause, { referencedTable: 'items' });
      const fallbackData = Array.isArray((fallback as any)?.data) ? (fallback as any).data : [];
      if (fallbackData.length > 0) {
        merged = fallbackData.slice(0, limit);
      }
    }
    return { data: merged, error: err };
  }

  if (sanitizedTerm) {
    const pattern = `%${sanitizedTerm.replace(/\s+/g, '%')}%`;
    if (itemColumns.length) {
      const itemSearchClause = itemColumns.map(col => `${col}.ilike.${pattern}`).join(',');
      const itemClause = excludedItemTypeClause
        ? `and(${excludedItemTypeClause},or(${itemSearchClause}))`
        : itemSearchClause;
      query = query.or(itemClause, { referencedTable: 'items' });
    }
    if (productColumns.length) {
      const productClause = productColumns.map(col => `${col}.ilike.${pattern}`).join(',');
      query = query.or(productClause, { referencedTable: 'products' });
    }
  }

  if (orderBy) {
    query = query.order(orderBy.column, {
      ascending: orderBy.ascending ?? true,
      referencedTable: orderBy.referencedTable,
    });
  }

  const { data, error } = await query;
  return { data: Array.isArray(data) ? data : [], error: error ?? null };
}

export async function upsertProductItemLink(params: {
  supabase?: SupabaseClient;
  payload: Record<string, any> | Record<string, any>[];
  onConflict?: string;
}): Promise<PostgrestError | null> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { payload, onConflict } = params;
  const { error } = await supabase.from('product_item_link').upsert(payload, onConflict ? { onConflict } : undefined);
  return error ?? null;
}

export async function deleteProductItemLinks(params: {
  supabase?: SupabaseClient;
  productId: string;
}): Promise<PostgrestError | null> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { productId } = params;
  const { error } = await supabase.from('product_item_link').delete().eq('product_id', productId);
  return error ?? null;
}

/**
 * Search the `items` table directly for kits (is_kit=true or ITA-KIT code)
 * that may not have a `product_item_link` row.  Returns raw item rows so the caller can merge
 * them into the standard ItemSearchResult set.
 */
export async function searchKitItemsDirect(params: {
  supabase?: SupabaseClient;
  searchTerm: string;
  categoryId?: string | null;
  limit?: number;
}): Promise<{ data: Array<Record<string, unknown>>; error: PostgrestError | null }> {
  const supabase = params.supabase ?? getSupabaseClient();
  const limit = params.limit ?? 50;
  const sanitized = (params.searchTerm ?? '')
    .replace(/[%]/g, '')
    .replace(/,/g, ' ')
    .replace(/&/g, ' ')
    .trim()
    .replace(/\s+/g, ' ')
    .trim();

  if (!sanitized) return { data: [], error: null };

  const pattern = `%${sanitized.replace(/\s+/g, '%')}%`;

  let query = supabase
    .from('items')
    .select(
      'item_id, item_code, name_internal, item_type_id, category_id, default_charge_days, is_kit, kit_pricing_mode, external_description, brand, model_name, model_number, manufacturer_name, purchase_cost_last, purchase_cost_avg, replacement_cost',
    )
    .or('is_kit.eq.true,item_code.ilike.ITA-KIT%')
    .is('deleted_at', null)
    .or(
      `name_internal.ilike.${pattern},item_code.ilike.${pattern},external_description.ilike.${pattern}`,
    )
    .limit(limit);

  if (params.categoryId) {
    query = query.eq('category_id', params.categoryId);
  }

  const { data, error } = await query;
  return { data: Array.isArray(data) ? (data as Array<Record<string, unknown>>) : [], error: error ?? null };
}
